/* ForgeTech two-product decision view — session-scoped and category-safe. */

(() => {
  const root = document.querySelector("[data-ft-compare-root]");
  if (!root) return;

  const storageKey = "forgetech:compare:v1";
  const tray = root.querySelector("[data-ft-compare-tray]");
  const dialog = root.querySelector("[data-ft-compare-dialog]");
  const grid = root.querySelector("[data-ft-compare-grid]");
  const count = root.querySelector("[data-ft-compare-count]");
  const action = root.querySelector("[data-ft-compare-action]");
  const status = root.querySelector("[data-ft-compare-status]");
  const notice = root.querySelector("[data-ft-compare-notice]");

  let products = read();
  let statusTimer;

  function read() {
    try {
      const value = JSON.parse(sessionStorage.getItem(storageKey) || "[]");
      return Array.isArray(value) ? value.filter((product) => product?.category).slice(0, 2) : [];
    } catch (_) {
      return [];
    }
  }

  function write() {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(products));
    } catch (_) {
      // Comparison still works for this page if storage is unavailable.
    }
  }

  function announce(message) {
    clearTimeout(statusTimer);
    status.textContent = "";
    status.hidden = false;
    requestAnimationFrame(() => {
      status.textContent = message;
    });
    statusTimer = window.setTimeout(() => {
      status.hidden = true;
    }, 4200);
  }

  function normalize(value) {
    return String(value || "").trim().toLowerCase();
  }

  function categoryFamily(value) {
    const category = normalize(value);
    if (!category) return "";
    if (category.includes("keycap")) return "keycaps";
    if (category.includes("keyboard")) return "keyboards";
    if (category.includes("mousepad") || category.includes("mouse pad") || category.includes("deskmat")) return "mousepads";
    if (category.includes("mouse") || category.includes("mice")) return "mice";
    if (category.includes("headset") || category.includes("headphone")) return "headsets";
    if (category.includes("earphone") || category.includes("earbud")) return "earphones";
    if (category.includes("controller") || category.includes("joystick") || category.includes("gamepad")) return "controllers";
    if (category.includes("cable") || category.includes("converter") || category.includes("adapter")) return "connectivity";
    return category;
  }

  function productFrom(button) {
    const data = button.dataset;
    return {
      id: String(data.productId || ""),
      handle: data.productHandle || "",
      url: data.productUrl || "#",
      title: data.productTitle || "Product",
      model: data.productModel || data.productTitle || "Product",
      summary: data.productSummary || "",
      vendor: data.productVendor || "",
      category: data.productCategory || "",
      bestFor: data.productBestFor || "",
      facts: data.productFacts || "",
      price: Number(data.productPrice || 0),
      available: data.productAvailable === "true",
      image: data.productImage || "",
    };
  }

  function sync() {
    tray.hidden = products.length === 0;
    count.textContent = `${products.length} / 2`;
    action.textContent = products.length === 2 ? "Compare now" : "Choose one more";

    document.querySelectorAll("[data-ft-compare-add]").forEach((button) => {
      const active = products.some((product) => product.id === button.dataset.productId);
      button.setAttribute("aria-pressed", String(active));
      button.setAttribute("aria-label", `${active ? "Remove" : "Add"} ${button.dataset.productTitle || "product"} ${active ? "from" : "to"} comparison`);
      const label = button.querySelector("[data-ft-compare-label]");
      if (label) label.textContent = active ? "Added" : "Compare";
    });
  }

  function add(button) {
    const candidate = productFrom(button);
    if (!categoryFamily(candidate.category)) {
      announce("Comparison is unavailable until this product has a category.");
      return;
    }
    const existingIndex = products.findIndex((product) => product.id === candidate.id);

    if (existingIndex >= 0) {
      products.splice(existingIndex, 1);
      write();
      sync();
      announce(`${candidate.model} removed from comparison.`);
      return;
    }

    if (products.length >= 2) {
      announce("Comparison is limited to two products. Clear one before adding another.");
      tray.animate?.([{ transform: "translateY(0)" }, { transform: "translateY(-4px)" }, { transform: "translateY(0)" }], { duration: 220 });
      return;
    }

    const firstCategory = categoryFamily(products[0]?.category);
    const candidateCategory = categoryFamily(candidate.category);
    if (firstCategory && candidateCategory && firstCategory !== candidateCategory) {
      announce(`Choose another ${products[0].category} to make a useful comparison.`);
      return;
    }

    products.push(candidate);
    write();
    sync();
    announce(`${candidate.model} added. ${products.length === 2 ? "Comparison is ready." : "Choose one more product."}`);
  }

  function money(cents) {
    const format = window.themeVariables?.settings?.moneyFormat || "${{amount}}";
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return format
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(Number(cents || 0) / 100)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace(".", ","))
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }

  function escapeHtml(value) {
    return String(value || "").replace(/[&<>"]/g, (character) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
    })[character]);
  }

  async function refresh(product) {
    if (!product.handle) return product;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || "/"}products/${product.handle}.js`, {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return product;
      const live = await response.json();
      const variant = live.variants?.find((item) => item.available) || live.variants?.[0];
      return {
        ...product,
        title: live.title || product.title,
        price: variant?.price ?? live.price ?? product.price,
        available: Boolean(live.available),
      };
    } catch (_) {
      return product;
    }
  }

  function row(label, value) {
    return `<div class="ft-compare__fact"><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value || "Not supplied")}</dd></div>`;
  }

  function card(product) {
    const availability = product.available ? "In stock" : "Currently unavailable";
    return `
      <article class="ft-compare__product">
        <a class="ft-compare__media" href="${escapeHtml(product.url)}">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" loading="lazy" width="640" height="480">` : ""}
        </a>
        <p class="ft-compare__vendor">${escapeHtml(product.vendor)}</p>
        <h3><a href="${escapeHtml(product.url)}" title="${escapeHtml(product.title)}">${escapeHtml(product.model)}</a></h3>
        ${product.summary ? `<p class="ft-compare__summary">${escapeHtml(product.summary)}</p>` : ""}
        <p class="ft-compare__price">${escapeHtml(money(product.price))}</p>
        <dl>
          ${row("Category", product.category)}
          ${row("Availability", availability)}
          ${row("Best for", product.bestFor)}
          ${row("Key facts", product.facts)}
        </dl>
        <a class="ft-compare__product-link" href="${escapeHtml(product.url)}">View product <span aria-hidden="true">›</span></a>
      </article>`;
  }

  async function open() {
    if (products.length < 2) {
      announce("Choose one more product before opening comparison.");
      return;
    }

    notice.textContent = "Refreshing live price and availability…";
    grid.setAttribute("aria-busy", "true");
    dialog.showModal();
    products = await Promise.all(products.map(refresh));
    write();
    grid.innerHTML = products.map(card).join("");
    grid.removeAttribute("aria-busy");
    notice.textContent = "Price and availability refreshed from the store.";
  }

  document.addEventListener("click", (event) => {
    const addButton = event.target.closest("[data-ft-compare-add]");
    if (addButton) {
      add(addButton);
      return;
    }

    if (event.target.closest("[data-ft-compare-open]")) open();
    if (event.target.closest("[data-ft-compare-close]")) dialog.close();
    if (event.target.closest("[data-ft-compare-clear]")) {
      products = [];
      write();
      sync();
      dialog.close();
      announce("Comparison cleared.");
    }
  });

  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close();
  });

  sync();
})();
